import { useLayoutEffect, useRef, type KeyboardEvent } from "react";
import { typingMoney } from "../../crm";

/**
 * A dollar amount: one box with the $ inside it, commas as you type ("12,500"),
 * and "12.5k" understood. With onCommit, Enter commits (blur) and Escape puts the old value back.
 * The parent parses with parseMoney().
 */
export default function MoneyInput({ value, onChange, onCommit, onRevert, label, disabled, placeholder = "0", autoFocus, saved }: {
  value: string;
  onChange: (value: string) => void;
  /** Called on blur and Enter, for fields that save as you leave them. */
  onCommit?: () => void;
  /** Escape: the parent restores the saved amount. */
  onRevert?: () => void;
  label?: string;
  disabled?: boolean;
  placeholder?: string;
  autoFocus?: boolean;
  /** Shows a quiet "Saved" inside the box for a moment. */
  saved?: boolean;
}) {
  const ref = useRef<HTMLInputElement>(null);
  // Where the caret goes after commas are added: after the same count of digits.
  const caret = useRef<number | null>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || caret.current === null || document.activeElement !== el) return;
    let seen = 0;
    let pos = 0;
    while (pos < el.value.length && seen < caret.current) { if (/[\d.kKmM]/.test(el.value[pos])) seen++; pos++; }
    el.setSelectionRange(pos, pos);
    caret.current = null;
  }, [value]);

  function keyDown(e: KeyboardEvent<HTMLInputElement>) {
    // A field that saves on its own commits on Enter; inside a form, Enter submits the form.
    if (e.key === "Enter" && onCommit) { e.preventDefault(); e.currentTarget.blur(); }
    if (e.key === "Escape" && onRevert) { e.preventDefault(); e.stopPropagation(); onRevert(); window.requestAnimationFrame(() => ref.current?.blur()); }
  }

  return (
    <span className="money-field">
      <span className="money-field-sign" aria-hidden="true">$</span>
      <input
        ref={ref}
        inputMode="decimal"
        autoComplete="off"
        aria-label={label}
        value={value}
        disabled={disabled}
        placeholder={placeholder}
        autoFocus={autoFocus}
        onChange={(e) => {
          const el = e.target;
          const left = el.value.slice(0, el.selectionStart ?? el.value.length);
          caret.current = left.replace(/[^\d.kKmM]/g, "").length;
          onChange(typingMoney(el.value));
        }}
        onBlur={onCommit}
        onKeyDown={keyDown}
      />
      {saved && <span className="money-field-saved" role="status">Saved</span>}
    </span>
  );
}
